import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { WorkflowDiagram } from "../components/WorkflowDiagram";
import { WorkflowPicker } from "../components/WorkflowPicker";
import { WorkflowSection } from "../components/WorkflowSection";
import { WorkflowTextDialog, type TextTarget } from "../components/WorkflowTextDialog";
import { useWorkflow, useWorkflowCatalog } from "../hooks/useWorkflows";
import type { DatasetMode, WorkflowSummary } from "../types";
import { stepTarget } from "../utils/textTarget";

/** The workflow in ?workflow=, else the workflow of an old ?mode= link, else the default mode's. */
const pickWorkflow = (
  workflows: WorkflowSummary[],
  modes: DatasetMode[],
  params: URLSearchParams,
): WorkflowSummary | undefined => {
  const byName = (name: string | null | undefined) =>
    workflows.find((workflow) => workflow.name === name);
  const linkedMode = modes.find((mode) => mode.id === params.get("mode"));
  return (
    byName(params.get("workflow")) ??
    byName(linkedMode?.workflow) ??
    byName(modes.find((mode) => mode.default)?.workflow) ??
    workflows[0]
  );
};

/** The workflows that the agent follows, read-only: their steps, the text of each step, and an example. */
export const WorkflowsPage = () => {
  useDocumentTitle("Workflows");
  const [searchParams, setSearchParams] = useSearchParams();
  const catalog = useWorkflowCatalog();
  const workflows = catalog.data?.workflows ?? [];
  const modes = catalog.data?.modes ?? [];
  const stages = catalog.data?.stages ?? [];
  const selected = pickWorkflow(workflows, modes, searchParams);
  const workflow = useWorkflow(selected?.name ?? null);
  const [opened, setOpened] = useState<TextTarget | null>(null);

  const selectWorkflow = (name: string) =>
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.set("workflow", name);
        next.delete("mode");
        return next;
      },
      { replace: true },
    );

  const detail = workflow.data?.name === selected?.name ? workflow.data : undefined;
  const openStep = (number: number) => {
    const step = detail?.steps.find((item) => item.number === number);
    if (detail && step) setOpened(stepTarget(detail, step));
  };

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="mx-auto max-w-6xl px-8 py-8">
        <header>
          <h1 className="font-display text-4xl leading-none tracking-tight text-zinc-900">
            Workflows
          </h1>
          <p className="mt-3 max-w-3xl text-base leading-7 text-zinc-600">
            A workflow is the list of steps that the agent follows for a task. Open a step to read
            the text that the agent gets for it. A planned workflow is a design for review. An
            archived workflow is an earlier version, kept to compare.
          </p>
        </header>

        {catalog.isLoading ? (
          <p className="mt-8 text-sm text-zinc-500" aria-live="polite">
            Loading the workflows…
          </p>
        ) : catalog.isError || !selected ? (
          <p role="alert" className="mt-8 text-sm text-zinc-600">
            Could not load the workflows. Reload the page to try again.
          </p>
        ) : (
          <>
            <div className="mt-8">
              <WorkflowPicker
                workflows={workflows}
                selected={selected}
                onSelect={selectWorkflow}
              />
            </div>
            <div className="mt-6">
              <WorkflowDiagram
                stages={stages}
                workflows={workflows}
                selected={selected}
                onSelect={selectWorkflow}
                onOpenStep={detail ? openStep : undefined}
              />
            </div>
            <div className="mt-8">
              {workflow.isLoading ? (
                <p className="text-sm text-zinc-500" aria-live="polite">
                  Loading the workflow…
                </p>
              ) : workflow.isError || !workflow.data ? (
                <p role="alert" className="text-sm text-zinc-600">
                  Could not load the workflow {selected.name}. Reload the page to try again.
                </p>
              ) : (
                <WorkflowSection
                  detail={workflow.data}
                  modes={modes.filter((mode) => mode.workflow === selected.name)}
                  onOpen={setOpened}
                />
              )}
            </div>
          </>
        )}
      </div>
      {opened && (
        <WorkflowTextDialog
          key={`${opened.title}-${opened.source ?? ""}`}
          target={opened}
          onClose={() => setOpened(null)}
        />
      )}
    </div>
  );
};
